/**
 * SiteApp — a space's public website, at /<space>/site and /<space>/site/<slug>.
 *
 * A page is a dashboard with a public URL: the Page item's stored View holds its modules, and each
 * module is drawn as a full-width section. Everything shown is read live from the space's items, so
 * editing an item is editing the website. The model and its rules are in siteModel.js; every setting
 * is declared in siteSettings.js; the vault notes "Site and Presentation Mode" and "Site Builder —
 * Display Settings Schema" carry the reasoning.
 *
 * NAVIGATION STAYS INSIDE THE APP. The router keys this route on the space alone, so moving between
 * pages is a pushState here rather than a new route — the space is not re-booted on every click.
 */
import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SiteApp.css';
import { useSiteData } from './useSiteData.js';
import { readSiteSettings, readFlat, readablePair, readableText, footerIsDark, PAGE_SCHEMA, SECTION_SCHEMA, MODULE_SCHEMA } from './siteSettings.js';
import { cf, idList, pageViewOf, headOf, resolveSiteCollection, reachableIds, itemForPath, itemPath, resolveLink, siteBase, presentOrder,
  siteShareUrl, valuesOf, wordsOf, sortSiteRows, filterChoices, filterMenu, filterDrawsAs, applyPicks, fieldLabel, linkedIds, isImageUrl, backlinksOf, videoOf, youtubeOf, youtubeThumb,
  youtubeEmbed, sitesOf, siteForPath, siteBaseOf, tableColumns, switchableViews, templatePageFor, templateTypeOf, fillTokens, storedViewSettings,
  timeOf, whenOf, metaWords, feedDays, browseValues, cardCountOf, typeGlyph, GOOGLE_CALENDAR_ICON, statValue } from './siteModel.js';
import { useVirtualizer } from '@tanstack/react-virtual';
import { renderChatMarkdown } from '../../utils/markdownChat.js';
import { outlineHtml, outlineTree, wrapTables } from '../../utils/markdownOutline.js';
import { EMBEDDABLE_VIEWS } from '../../views/embeddable.js';
import SiteLoading from './SiteLoading.jsx';
import SiteWeekArt from './SiteWeekArt.jsx';
import { siteColorKey, dismissHubSplash } from './siteFetch.js';
import { recordSiteVisit } from './siteVisit.js';
import { pageTitle, labelMentions } from './siteMeta.js';

// Both load only when used: Present when `?present` is in the address, the edit button only for a
// browser that already holds an Arcos sign-in. A visitor downloads neither.
const SitePresent = lazy(() => import('./SitePresent.jsx'));
const EditSitePill = lazy(() => import('./EditSitePill.jsx'));
// A form opens over the page (SiteFormPopup), loaded when a visitor first points at a form's button.
const loadFormPopup = () => import('./SiteFormPopup.jsx');
const SiteFormPopup = lazy(loadFormPopup);
// A PDF or a picture opens over the page (SiteFileViewer) when the site or the button says so; it and
// pdf.js load only when a visitor first points at such a link.
const loadFileViewer = () => import('./SiteFileViewer.jsx');
const SiteFileViewer = lazy(loadFileViewer);
// Favorites (siteFavorites.js) and signing in to save (SiteSignIn.jsx) load when a visitor first meets a
// heart or the Saved chip: they bring the Supabase client and the app's LoginForm.
const loadFavoritesApi = () => import('./siteFavorites.js');
// A View Type drawn by its own canvas (src/views/embeddable.js), loaded when a visitor first shows it.
const SiteMapView = lazy(EMBEDDABLE_VIEWS.map.load);
const SiteCalendarView = lazy(EMBEDDABLE_VIEWS.calendar.load);
const SiteSignIn = lazy(() => import('./SiteSignIn.jsx'));
// Syncing to Google Calendar (SiteSync.jsx) brings the Supabase client too, so it loads only for a
// visitor who holds a sign-in or has just signed in to sync; anyone else sees a plain button.
const SiteSyncLive = lazy(() => import('./SiteSync.jsx'));
/** Does this browser hold an Arcos sign-in? A local read: no request, no Supabase client. */
const hasStoredSession = () => {
  try { return Object.keys(localStorage).some((k) => /^sb-.*-auth-token$/.test(k)); } catch { return false; }
};
/** Where a form opens when the site is on its own domain: forms live in the Arcos app. */
const APP_ORIGIN = 'https://www.arcos.network';
/** A collection shown as a slide stops here, with a line saying how many more the page holds. */
const PRESENT_CARDS = 6;

const RADIUS = { none: '0px', sm: '4px', md: '8px', lg: '16px' };
const WIDTH = { narrow: '760px', normal: '1120px', wide: '1320px' };
const NEXT_WEIGHT = { filled: 'outline', outline: 'text', text: 'text' };
const VIEW_LABEL = { table: 'Table', list: 'List', cards: 'Cards', map: 'Map', feed: 'Feed', calendar: 'Calendar' };
const GOOGLE_FONTS = { Sen: 'Sen:wght@400;700;800', Inter: 'Inter:wght@400;600;800' };

/** Load a theme font once, when the site asks for one the app does not already carry. */
function useThemeFonts(fonts) {
  useEffect(() => {
    for (const f of fonts) {
      if (!GOOGLE_FONTS[f]) continue;
      const id = `site-font-${f}`;
      if (document.getElementById(id)) continue;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONTS[f]}&display=swap`;
      document.head.appendChild(link);
    }
  }, [fonts]);
}

const fontStack = (f) => (f === 'system-ui' ? 'system-ui, sans-serif' : `'${f}', system-ui, sans-serif`);
// markdown-it with html:false, then DOMPurify — the path the dashboard's info module uses. A table is
// boxed so a wide one scrolls inside itself rather than pushing the page wider than the screen. Its
// mentions read as their names (siteMeta.labelMentions) before markdown runs, which has no rule for them.
const siteMarkdown = (text) => wrapTables(renderChatMarkdown(labelMentions(text || '')), 'site-md-table');
const Markdown = ({ text, className, html = null }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: html ?? siteMarkdown(text) }} />
);

/** Fewer headings than this and a body reads without a table of contents. */
const CONTENTS_MIN = 4;

/**
 * A long body's table of contents (detail.contents_by_type): its top headings, and under the one the
 * reader is in, the level below — so a 135-page agreement lists its articles, and the sections of the
 * article being read. Beside the body on a wide screen, staying in view; above it on a narrow one, as a
 * list that opens. A click glides to the heading and puts its address in the bar, as an in-page button
 * does, without adding a step to Back.
 */
function Contents({ tree, ctx }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const ids = useMemo(() => tree.flatMap((t) => [t.id, ...t.children.map((c) => c.id)]).filter(Boolean), [tree]);
  useEffect(() => {
    // The heading the reader is in: the last one whose top has passed under the sticky header. Read on
    // any scroll (capture, so the editor's scrolling preview counts too), once a frame at most.
    let frame = 0;
    const read = () => {
      frame = 0;
      let current = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - 120 > 0) break;
        current = id;
      }
      setActive(current);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(read); };
    read();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => { document.removeEventListener('scroll', onScroll, { capture: true }); if (frame) cancelAnimationFrame(frame); };
  }, [ids]);
  const activeTop = tree.find((t) => t.id === active || t.children.some((c) => c.id === active)) || null;
  const go = (e, id) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || ctx.editing) return;
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(window.history.state, '', `#${id}`);
    setOpen(false);
  };
  const link = (h, cls) => (
    <a href={`#${h.id}`} className={`${cls}${h.id === active ? ' is-active' : ''}`} aria-current={h.id === active ? 'location' : undefined} onClick={(e) => go(e, h.id)}>{h.text}</a>
  );
  return (
    <nav className={`site-toc${open ? ' is-open' : ''}`} aria-label="Contents">
      <button type="button" className="site-toc-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>Contents</span><span className="site-toc-where">{activeTop?.text || ''}</span>
      </button>
      <div className="site-toc-head">Contents</div>
      <ol className="site-toc-list">
        {tree.map((t) => (
          <li key={t.id || 'start'}>
            {t.id && link(t, 'site-toc-top')}
            {(t === activeTop || !t.id) && t.children.length > 0 && (
              <ol>{t.children.map((c) => <li key={c.id}>{link(c, 'site-toc-sub')}</li>)}</ol>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * Hands `url` to the device's share sheet, or copies it where there is none (most desktop browsers).
 * Resolves to 'shared', 'copied' or 'none' — 'none' when the visitor closed the sheet, or when even
 * copying failed and the address was shown to copy by hand.
 */
async function shareAddress(url, title) {
  if (typeof navigator.share === 'function') {
    try { await navigator.share({ title, url }); return 'shared'; }
    catch (e) { if (e?.name === 'AbortError') return 'none'; }   // closed; any other refusal falls through to copying
  }
  try { await navigator.clipboard.writeText(url); return 'copied'; }
  catch { window.prompt('Copy this link', url); return 'none'; }
}

function SiteLink({ link, ctx, className, children }) {
  const r = resolveLink(link, ctx);
  const [copied, setCopied] = useState(false);
  const external = r.external || !!r.form;
  // A form opens over the page rather than in a new tab. The address stays a real link, so a middle
  // click, a modified click or a page without JavaScript still opens the form's own page.
  const popup = !!(r.form && ctx.openForm && !ctx.editing);
  // A file opens over the page the same way, and stays a real link for the same reasons.
  const viewer = !!(r.file && ctx.openFile && !ctx.editing);
  const plainClick = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
  const onClick = popup ? (e) => {
    if (!plainClick(e)) return;
    e.preventDefault();
    ctx.openForm(r.form, r.href);
  } : viewer ? (e) => {
    if (!plainClick(e)) return;
    e.preventDefault();
    ctx.openFile({ href: r.href, kind: r.file, title: link.fileTitle || link.label });
  } : r.href.startsWith('#') && r.href.length > 1 ? (e) => {
    // A jump within the page glides there rather than cutting, unless the visitor asks for less motion.
    if (!plainClick(e) || ctx.editing) return;
    const target = document.getElementById(decodeURIComponent(r.href.slice(1)));
    if (!target) return;
    e.preventDefault();
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(window.history.state, '', r.href);
  } : r.share ? (e) => {
    // A Share button: the site's address to the share sheet, or copied, saying so for two seconds.
    // In the editor it does nothing, as a click there selects rather than follows.
    if (ctx.editing) { e.preventDefault(); return; }
    if (!plainClick(e)) return;
    e.preventDefault();
    shareAddress(r.href, ctx.siteTitle).then((done) => {
      if (done !== 'copied') return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  } : undefined;
  return (
    <a className={className} href={r.href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}
      onClick={onClick} onPointerEnter={popup ? loadFormPopup : viewer ? loadFileViewer : undefined} onFocus={popup ? loadFormPopup : viewer ? loadFileViewer : undefined}
      aria-live={r.share ? 'polite' : undefined}>
      {copied ? 'Link copied' : children ?? link.label}
    </a>
  );
}

/**
 * An item named somewhere else on the site — an event's styles, who puts it on, a credit — as a pill: its
 * logo, or its type's icon when it has none, then its title. It opens the item's page when the site has
 * one, else its web address, unless `link` says otherwise.
 */
function ItemPill({ row, ctx, link = undefined, className = '', hidden = false }) {
  const type = ctx.typesById?.get(row.item_type_id);
  const logo = cf(row).image_logo || null;
  const to = link !== undefined ? link
    : ctx.reach.has(row.id) ? { label: row.title, itemId: row.id } : row.url ? { label: row.title, href: row.url } : null;
  const inner = (
    <>
      <span className={`site-pill-icon${logo ? ' has-logo' : ''}`}><img src={logo || typeGlyph(type)} alt="" loading="lazy" /></span>
      <span className="site-pill-word">{row.title}</span>
    </>
  );
  const cls = `site-pill${className ? ` ${className}` : ''}`;
  if (hidden) return <span className={cls} aria-hidden="true">{inner}</span>;
  return to ? <SiteLink link={to} ctx={ctx} className={cls}>{inner}</SiteLink> : <span className={cls}>{inner}</span>;
}

function Buttons({ buttons, firstStyle, ctx, small = false }) {
  if (!buttons || !buttons.length) return null;
  let weight = firstStyle;
  return (
    <div className="site-buttons">
      {buttons.map((b, i) => {
        const cls = `site-btn site-btn--${weight}${small ? ' site-btn--sm' : ''}`;
        if (i === 0) weight = NEXT_WEIGHT[weight];
        return <SiteLink key={`${b.label}-${i}`} link={b} ctx={ctx} className={cls} />;
      })}
    </div>
  );
}

/**
 * A video that costs nothing until asked for: its picture and a play button, then YouTube's
 * privacy-enhanced player in the same place. A page of eight talks loads eight pictures, not eight players.
 */
function VideoPlayer({ video, cover, title, className = '' }) {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className={`site-video ${className}`}>
        <iframe src={youtubeEmbed(video)} title={title || 'Video'} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
      </div>
    );
  }
  return (
    <button type="button" className={`site-video site-video--idle ${className}`} onClick={() => setPlaying(true)} aria-label={`Play ${title || 'the video'}`}>
      <img src={cover || youtubeThumb(video.id)} alt="" loading="lazy" />
      <span className="site-video-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" fill="currentColor" /></svg></span>
    </button>
  );
}

/** A stored date as a visitor reads it. A bare `YYYY-MM-DD` is a calendar day, so it is read in UTC. */
function formatDate(value, style = 'long') {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const opts = style === 'month' ? { year: 'numeric', month: 'short' } : { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: 'UTC' }).format(d);
}

const HEART_PATH = 'M12 20.5s-7.5-4.4-7.5-10A4.5 4.5 0 0 1 12 7.6a4.5 4.5 0 0 1 7.5 2.9c0 5.6-7.5 10-7.5 10z';

/**
 * The heart: save an item to the visitor's Arcos account, or unsave it. A visitor who is not signed in
 * is asked to first (`ctx.fav.toggle` opens the dialog and finishes the save after it). Nothing in the
 * editor's preview or in Present, where a click means something else.
 */
function Heart({ row, ctx, className = '' }) {
  const fav = ctx.fav || null;
  useEffect(() => { fav?.ensure(); }, [fav]);
  if (!fav || !row) return null;
  const on = fav.ids.has(row.id);
  return (
    <button type="button" className={`site-heart${on ? ' is-on' : ''}${fav.busy.has(row.id) ? ' is-busy' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={on} aria-label={on ? `Saved — ${row.title}` : `Save ${row.title}`} title={on ? 'Saved' : 'Save'}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); fav.toggle(row); }}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d={HEART_PATH} fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
    </button>
  );
}

/**
 * "Sync to Google Calendar" for one collection. A visitor with no sign-in in this browser sees a plain
 * button that asks them to sign in (the site's own dialog) and then hands on to the live control, which
 * connects Google and syncs; a visitor already signed in gets the live control at once. Never in Present
 * or the editor, where a click means something else.
 */
function SyncButton({ collectionId, ctx, label = 'Sync to Google Calendar', weight = 'outline', small = false }) {
  const [live, setLive] = useState(() => hasStoredSession());
  const [round, setRound] = useState({ n: 0, start: false });
  if (!collectionId || ctx.present || !ctx.fav) return null;
  const signIn = () => ctx.fav.askToSignIn({
    title: 'Sync this calendar',
    line: 'Sign in, or make a free Arcos account, and connect Google Calendar. New events then arrive in your calendar by themselves.',
    mark: 'calendar',
    after: () => { setLive(true); setRound((r) => ({ n: r.n + 1, start: true })); },
  });
  const cls = `site-btn site-btn--${weight}${small ? ' site-btn--sm' : ''} site-sync-btn`;
  const plain = <span className="site-sync"><button type="button" className={cls} onClick={signIn}><img className="site-sync-gcal" src={GOOGLE_CALENDAR_ICON} alt="" />{label}</button></span>;
  if (!live) return plain;
  return (
    <Suspense fallback={plain}>
      <SiteSyncLive key={round.n} collectionId={collectionId} label={label} weight={weight} small={small} start={round.start} onSignIn={signIn} />
    </Suspense>
  );
}

/** Where a row's title goes on the site: its own page when the site reaches it, else its web address. */
function titleLinkOf(r, m, ctx) {
  const reached = ctx.reach.has(r.id);
  const click = m.cardClick === 'auto' ? (reached ? 'detail' : 'url') : m.cardClick;
  return click === 'detail' && reached ? { label: r.title, itemId: r.id } : click === 'url' && r.url ? { label: r.title, href: r.url } : null;
}

/**
 * The feed: rows under a heading for each day, each with its start time, title, details and chips, and
 * its picture small at the end — what is on, readable one-handed on a phone. The app's event feed, drawn
 * in the site's theme.
 */
function Feed({ rows, m, ctx, fav, chipField }) {
  const days = useMemo(() => feedDays(rows), [rows]);
  const details = m.metaFields.filter((f) => f !== 'date_start' && f !== 'Date Start');
  return (
    <div className="site-feed">
      {days.map((day) => (
        <section key={day.key} className="site-feed-day" aria-label={day.label ? `${day.label}, ${day.date}` : day.date}>
          <h3 className="site-feed-dayhead">{day.label && <><b>{day.label}</b><span aria-hidden="true">·</span></>}<span>{day.date}</span></h3>
          <ul className="site-feed-rows">
            {day.rows.map((r) => {
              const link = titleLinkOf(r, m, ctx);
              const meta = metaWords(r, details, ctx.rowsById);
              const tags = wordsOf(r, chipField, ctx.rowsById);
              const count = m.cardCount?.collection ? cardCountOf(r, m.cardCount, ctx) : null;
              const pic = r.image_cover_url || cf(r).image_logo || null;
              return (
                <li key={r.id} className={`site-feed-row${link && m.cardLinkArea === 'card' ? ' site-card--whole' : ''}`}>
                  <div className="site-feed-time">{timeOf(r.date_start) || (r.date_start ? 'All day' : '')}</div>
                  <div className="site-feed-main">
                    {link ? <SiteLink link={link} ctx={ctx} className={`site-feed-title${m.cardLinkArea === 'card' ? ' site-card-cover' : ''}`} /> : <div className="site-feed-title">{r.title}</div>}
                    {(meta.length > 0 || count > 0) && (
                      <div className="site-feed-meta">{[...meta, count > 0 ? countWords(count, m.cardCount) : null].filter(Boolean).join(' · ')}</div>
                    )}
                    {tags.length > 0 && <div className="site-card-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div>}
                  </div>
                  {pic && <img className="site-feed-thumb" src={pic} alt="" loading="lazy" />}
                  {fav && <Heart row={r} ctx={ctx} className="site-feed-heart" />}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * A marquee: every row as a pill, in one line that glides sideways and pauses under the pointer — the
 * credits for a site's sources or partners. The line is drawn twice so it loops without a seam; the second
 * copy is hidden from screen readers, and for a visitor who asks for less motion the line stands still and
 * wraps. Pills open what a card's title would (`cardClick`).
 */
function Marquee({ rows, m, ctx }) {
  if (!rows.length) return null;
  const seconds = Math.max(24, rows.length * 4);
  return (
    <div className="site-marquee" style={{ '--site-marquee-time': `${seconds}s` }}>
      <div className="site-marquee-track">
        {rows.map((r) => <ItemPill key={r.id} row={r} ctx={ctx} link={titleLinkOf(r, m, ctx)} />)}
        {rows.map((r) => <ItemPill key={`again-${r.id}`} row={r} ctx={ctx} hidden className="site-marquee-again" />)}
      </div>
    </div>
  );
}

/**
 * A section's live numbers (`stats`): each counted from the site's own items as the page loads — so the
 * strip is as current as the site — and rolled up from nought the first time it scrolls into view, briefly,
 * unless the visitor asks for less motion. A number with a link opens it.
 */
function Stats({ stats, ctx }) {
  const items = useMemo(() => stats.map((s) => ({ s, n: statValue(s, ctx) })).filter((x) => x.n != null), [stats, ctx]);
  const ref = useRef(null);
  const [shown, setShown] = useState(() => (ctx.present || ctx.editing ? 1 : 0));
  const settled = shown === 1;
  // Once, on mount: watch for the strip to come into view, then roll the numbers up.
  useEffect(() => {
    if (settled || !ref.current) return undefined;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still || typeof IntersectionObserver === 'undefined') { setShown(1); return undefined; }
    let raf = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const step = (t) => { const p = Math.min(1, (t - t0) / 900); setShown(1 - (1 - p) ** 3); if (p < 1) raf = requestAnimationFrame(step); };
      raf = requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(ref.current);
    // A hidden tab draws no frames: never leave the numbers at nought.
    const fallback = setTimeout(() => setShown(1), 2500);
    return () => { io.disconnect(); cancelAnimationFrame(raf); clearTimeout(fallback); };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps -- runs once; `settled` only says whether to start
  if (!items.length) return null;
  return (
    <div className="site-stats" ref={ref}>
      {items.map(({ s, n }) => {
        const shownN = Math.round(n * shown);
        const words = n === 1 && s.one ? s.one : s.label || '';
        const inner = (
          <>
            <span className="site-stat-n" aria-hidden="true">{shownN.toLocaleString()}</span>
            <span className="site-stat-label" aria-hidden="true">{words}</span>
            <span className="site-sr">{`${n.toLocaleString()} ${words}`}</span>
          </>
        );
        const to = s.itemId ? { label: words, itemId: s.itemId } : s.href ? { label: words, href: s.href } : null;
        return to
          ? <SiteLink key={`${s.collection}-${words}`} link={to} ctx={ctx} className="site-stat site-stat--link">{inner}</SiteLink>
          : <div key={`${s.collection}-${words}`} className="site-stat">{inner}</div>;
      })}
    </div>
  );
}

/** What a search box counts: the rows' own type ("events") when they share one, else "items". */
function searchWord(rows, ctx) {
  const ids = new Set(rows.map((r) => r.item_type_id));
  const t = ids.size === 1 ? ctx.typesById?.get([...ids][0]) : null;
  return (rows.length === 1 ? t?.display_singular : t?.display)?.toLowerCase() || (rows.length === 1 ? 'item' : 'items');
}

/** "12 upcoming events", or `one` for a single one ("1 upcoming event"). */
const countWords = (n, cc = {}) => `${n} ${n === 1 && cc.one ? cc.one : cc.label || 'items'}`;

const MENU_WIDTH = 300;

/**
 * One filter as a chip that opens a menu: every value with a tick box and how many rows it would keep
 * under the other filters, then Clear and "Show N". The community app's filter menus, drawn in the
 * site's own theme. The chip says the field's name with nothing ticked, the value with one, and
 * "Name · 2" with several.
 *
 * The menu hangs under its chip inside the page rather than over the window: the site root is a
 * container, so a fixed panel would be placed against the whole page. It is nudged sideways to stay
 * inside the window, and closes on Escape or a press anywhere else.
 */
function FilterMenu({ field, label, rows, rowsById, picks, setPicks, shown }) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState({ left: 0, width: MENU_WIDTH });
  const ref = useRef(null);
  const picked = picks[field] || [];
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const toggleOpen = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const width = Math.min(MENU_WIDTH, vw - 32);
      let left = 0;
      if (r.left + width > vw - 16) left = vw - 16 - (r.left + width);
      if (r.left + left < 16) left = 16 - r.left;
      setPlace({ left, width });
    }
    setOpen((o) => !o);
  };
  const options = open ? filterMenu(rows, field, picks, rowsById) : [];
  const toggle = (v) => setPicks((p) => {
    const cur = p[field] || [];
    return { ...p, [field]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });
  const word = picked.length === 0 ? label : picked.length === 1 ? picked[0] : `${label} · ${picked.length}`;
  return (
    <div className="site-filter-dd" ref={ref}>
      <button type="button" className={`site-chip site-chip--dd${picked.length ? ' is-on' : ''}`} aria-expanded={open} aria-haspopup="true"
        title={picked.length ? `${label}: ${picked.join(', ')}` : undefined} onClick={toggleOpen}>
        <span className="site-chip-word">{word}</span>
        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div className="site-filter-panel" role="group" aria-label={label} style={{ left: place.left, width: place.width }}>
          <div className="site-filter-panel-head">{label}</div>
          <div className="site-filter-panel-opts">
            {options.map((o) => {
              const on = picked.includes(o.value);
              return (
                <button key={o.value} type="button" className={`site-filter-opt${on ? ' is-on' : ''}${o.count === 0 && !on ? ' is-off' : ''}`}
                  aria-pressed={on} onClick={() => toggle(o.value)}>
                  <span className="site-filter-box" aria-hidden="true" />
                  <span className="site-filter-opt-word">{o.value}</span>
                  <span className="site-filter-opt-count">{o.count}</span>
                </button>
              );
            })}
          </div>
          <div className="site-filter-panel-foot">
            <button type="button" className="site-btn site-btn--outline site-btn--sm" onClick={() => setPicks((p) => ({ ...p, [field]: [] }))}>Clear</button>
            <button type="button" className="site-btn site-btn--filled site-btn--sm" onClick={() => setOpen(false)}>Show {shown}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Cards — a collection's rows, or the cards written into an info module. A collection can offer a
 * search box and one row of choices per `filters` field; both narrow the rows before `max` caps them,
 * and the first filter field's values are what each card shows as its chips.
 *
 * `tools` are the section's own buttons ("Add a group"), drawn at the end of the search row rather
 * than under the heading, so the list's controls read as one line above the list.
 */
function Cards({ module, rows, written, firstStyle, ctx, tools = [], align = 'left' }) {
  const m = readFlat(module, MODULE_SCHEMA);
  const filterFields = useMemo(() => (written ? [] : m.filters), [written, m.filters]);
  const [query, setQuery] = useState('');
  // `{ field: [values] }`. A pills row picks one value at a time, a menu several.
  const [picks, setPicks] = useState({});
  // The Saved chip: only what this visitor saved. Hearts and the chip are the module's `favorites`.
  const fav = !written && m.favorites ? ctx.fav || null : null;
  const [savedOnly, setSavedOnly] = useState(false);
  useEffect(() => { fav?.ensure(); }, [fav]);
  const rowsById = ctx.rowsById;
  const choices = useMemo(() => filterFields.map((f) => ({ field: f, options: filterChoices(rows, f, rowsById) }))
    .filter((c) => c.options.length > 1)
    .map((c) => ({ ...c, as: filterDrawsAs(m.filterStyle, c.options.length) })), [rows, filterFields, m.filterStyle, rowsById]);
  // `max` is how many show at first, not how many there are: search and filters read every row, and
  // "Show more" opens the next `max` at a time, so a long list keeps the page short without hiding
  // anything. 0 shows every row from the start.
  const [limit, setLimit] = useState(m.max);
  useEffect(() => { setLimit(m.max); }, [m.max]);   // a new cap from the site editor applies at once
  // Search first, then the picks, so a menu can count its values within what the search left.
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => `${r.title || ''} ${r.description || r.text || ''}`.toLowerCase().includes(q)) : rows;
  }, [rows, query]);
  const savedIds = fav?.ids;
  const matched = useMemo(() => {
    if (written) return rows;
    const picked = applyPicks(searched, picks, rowsById);
    return savedOnly && savedIds ? picked.filter((r) => savedIds.has(r.id)) : picked;
  }, [rows, written, searched, picks, savedOnly, savedIds, rowsById]);
  const shown = !written && limit > 0 ? matched.slice(0, limit) : matched;
  const pick = (field, value) => setPicks((p) => ({ ...p, [field]: (p[field] || []).length === 1 && p[field][0] === value ? [] : [value] }));
  const cardWeight = NEXT_WEIGHT[firstStyle];
  const whole = m.cardLinkArea === 'card';
  const chipField = filterFields[0] || 'tags';
  const more = !written && ctx.present ? Math.max(0, matched.length - PRESENT_CARDS) : 0;
  const visible = more ? matched.slice(0, PRESENT_CARDS) : shown;
  const hidden = !written && !ctx.present ? matched.length - shown.length : 0;
  // The views a visitor may switch between (`views`), starting on the module's own. A table scrolls
  // inside itself, so it shows every match and needs no "Show more".
  const views = written ? [] : switchableViews(module);
  const [picked, setPicked] = useState(null);
  const viewType = picked && views.includes(picked) ? picked : m.viewType;
  // The module's own "Sync to Google Calendar": its collection, put into the visitor's calendar.
  const syncId = written ? null
    : m.sync === 'collection' ? (module.source?.collection === '$context' ? ctx.contextItem?.id : module.source?.collection) || null
    : m.sync === 'context' && ctx.contextItem?.collection_source ? ctx.contextItem.id : null;
  const cardSync = !written && (Array.isArray(m.cardButtons) ? m.cardButtons : []).find((cb) => cb && cb.field === 'sync') || null;
  const syncFirst = !!cardSync && m.cardButtons[0] === cardSync;
  const anyPic = !written && viewType === 'cards' && visible.some((r) => r.image_cover_url || cf(r).image_logo);
  return (
    <div className="site-module-body">
      {!written && (m.search || choices.length > 0 || tools.length > 0 || views.length > 0 || fav || syncId) && (
        <div className={`site-cards-tools site-cards-tools--${align}`}>
          {m.search && (
            <input type="search" className="site-search" placeholder={`Search ${rows.length} ${searchWord(rows, ctx)}`} aria-label="Search"
              value={query} onChange={(e) => setQuery(e.target.value)} />
          )}
          {fav && (
            <button type="button" className={`site-chip site-chip--saved${savedOnly ? ' is-on' : ''}`} aria-pressed={savedOnly}
              onClick={async () => {
                if (fav.account || await fav.ensure()) setSavedOnly((v) => !v);
                else fav.askToSignIn({ title: 'See what you saved', after: () => setSavedOnly(true) });
              }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d={HEART_PATH} fill={savedOnly ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
              Saved{fav.account && fav.ids.size ? ` · ${rows.filter((r) => fav.ids.has(r.id)).length}` : ''}
            </button>
          )}
          {choices.some((c) => c.as === 'menu') && (
            <div className="site-filter-menus">
              {choices.filter((c) => c.as === 'menu').map(({ field }) => (
                <FilterMenu key={field} field={field} label={m.fieldLabels[field] || fieldLabel(field, ctx.fieldNames)} rows={searched} rowsById={rowsById}
                  picks={picks} setPicks={setPicks} shown={matched.length} />
              ))}
            </div>
          )}
          {choices.filter((c) => c.as === 'pills').map(({ field, options }) => {
            const on = picks[field] || [];
            return (
              <div key={field} className="site-filter" role="group" aria-label={m.fieldLabels[field] || fieldLabel(field, ctx.fieldNames)}>
                <button type="button" className={`site-chip${on.length ? '' : ' is-on'}`} aria-pressed={!on.length}
                  onClick={() => setPicks((p) => ({ ...p, [field]: [] }))}>All</button>
                {options.map((o) => (
                  <button key={o.value} type="button" className={`site-chip${on.includes(o.value) ? ' is-on' : ''}`}
                    aria-pressed={on.includes(o.value)} onClick={() => pick(field, o.value)}>{o.value}</button>
                ))}
              </div>
            );
          })}
          {views.length > 0 && (
            <div className="site-viewswitch" role="group" aria-label="View">
              {views.map((v) => (
                <button key={v} type="button" className={`site-viewswitch-btn${viewType === v ? ' is-on' : ''}`} aria-pressed={viewType === v}
                  onClick={() => setPicked(v)}>{VIEW_LABEL[v] || v}</button>
              ))}
            </div>
          )}
          <Buttons buttons={tools} firstStyle={firstStyle} ctx={ctx} small />
          {syncId && <SyncButton collectionId={syncId} ctx={ctx} small />}
        </div>
      )}
      {!written && shown.length === 0 && <p className="site-cards-empty">{savedOnly ? 'Nothing saved here yet. Tap a heart to keep something.' : 'Nothing matches that yet.'}</p>}
      {!written && viewType === 'table' && matched.length > 0 && (
        <SiteTable module={module} rows={more ? visible : matched} capped={more > 0} ctx={ctx} />
      )}
      {!written && viewType === 'map' && <SiteMap module={module} rows={matched} ctx={ctx} />}
      {!written && viewType === 'feed' && <Feed rows={visible} m={m} ctx={ctx} fav={fav} chipField={chipField} />}
      {!written && viewType === 'marquee' && <Marquee rows={matched} m={m} ctx={ctx} />}
      {!written && viewType === 'calendar' && <SiteCalendar rows={matched} ctx={ctx} />}
      {(written || (viewType !== 'table' && viewType !== 'map' && viewType !== 'feed' && viewType !== 'marquee' && viewType !== 'calendar')) && (
      <div className={`site-cards site-cards--${viewType === 'video' ? 'cards site-cards--video' : viewType} site-cols--${m.columns}`}>
        {visible.map((r, i) => {
          if (written) {
            return (
              <article className={`site-card${whole && r.link ? ' site-card--whole' : ''}`} key={`${r.title}-${i}`}>
                {r.image && <div className={`site-card-img site-card-img--logo site-card-tone--${m.cardTone}`}><img src={r.image} alt="" loading="lazy" /></div>}
                <div className="site-card-body">
                  <div className="site-card-title">{r.title}</div>
                  {r.text && <div className="site-card-desc">{labelMentions(r.text)}</div>}
                  {/* A typed card's one button is its link, so on a whole card it is what the card opens. */}
                  {r.link && <div className="site-card-btns"><SiteLink link={r.link} ctx={ctx} className={`site-btn site-btn--${cardWeight} site-btn--sm${whole ? ' site-card-cover' : ''}`} /></div>}
                </div>
              </article>
            );
          }
          const logo = cf(r).image_logo || null;
          const useLogo = m.cardImage === 'logo' || (m.cardImage === 'auto' && !r.image_cover_url);
          const src = useLogo ? (logo || r.image_cover_url) : (r.image_cover_url || logo);
          const reached = ctx.reach.has(r.id);
          const titleLink = titleLinkOf(r, m, ctx);
          const tags = wordsOf(r, chipField, rowsById);
          const meta = metaWords(r, m.metaFields, rowsById);
          const count = m.cardCount?.collection ? cardCountOf(r, m.cardCount, ctx) : null;
          const buttons = (Array.isArray(m.cardButtons) ? m.cardButtons : []).map((cb) => {
            if (!cb || !cb.label || cb.field === 'sync') return null;
            if (cb.field === 'page') return reached ? { label: cb.label, itemId: r.id } : null;
            const v = cb.field === 'url' ? r.url : cf(r)[cb.field];
            if (typeof v !== 'string' || !/^(https?:|mailto:)/.test(v)) return null;
            // `open` is the button's own say on a file (siteModel.resolveLink); the viewer names it after its card.
            return { label: cb.label, href: v, fileTitle: `${r.title} · ${cb.label}`, ...(cb.open ? { open: cb.open } : {}) };
          }).filter(Boolean);
          const video = viewType === 'video' ? videoOf(r) : null;
          // In a grid where other cards have a picture, one without keeps the place, its type's icon in it,
          // so the row lines up and the card still reads as a card.
          const stand = !src && !video && anyPic;
          const wholeCard = whole && titleLink && viewType !== 'video';
          return (
            <article className={`site-card${wholeCard ? ' site-card--whole' : ''}`} key={r.id}>
              {stand && <div className={`site-card-img site-card-img--empty site-card-tone--${m.cardTone}`}><img src={typeGlyph(ctx.typesById?.get(r.item_type_id))} alt="" /></div>}
              {video
                ? <VideoPlayer video={video} cover={r.image_cover_url} title={r.title} className="site-card-img" />
                : src && <div className={`site-card-img${useLogo ? ' site-card-img--logo' : m.cardImageFit === 'whole' ? ' site-card-img--whole' : ''} site-card-tone--${m.cardTone}`}><img src={src} alt="" loading="lazy" /></div>}
              <div className="site-card-body">
                {/* The words, apart from the buttons, so a list row can put its buttons beside them. */}
                <div className="site-card-main">
                  {viewType === 'video' && r.date_start && <div className="site-card-meta">{formatDate(r.date_start, 'month')}</div>}
                  {titleLink ? <SiteLink link={titleLink} ctx={ctx} className={`site-card-title${wholeCard ? ' site-card-cover' : ''}`} /> : <div className="site-card-title">{r.title}</div>}
                  {meta.length > 0 && <div className="site-card-details">{meta.join(' · ')}</div>}
                  {count > 0 && <div className="site-card-count">{countWords(count, m.cardCount)}</div>}
                  {tags.length > 0 && <div className="site-card-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div>}
                  {r.description && <div className="site-card-desc">{labelMentions(r.description)}</div>}
                </div>
                {(buttons.length > 0 || (cardSync && r.collection_source)) && (
                  <div className="site-card-btns">
                    {/* Sync takes the place it has in `cardButtons`: first, it is the card's main button. */}
                    {syncFirst && r.collection_source && <SyncButton collectionId={r.id} ctx={ctx} label={cardSync.label} weight={cardWeight} small />}
                    {buttons.map((b, i) => <SiteLink key={b.label} link={b} ctx={ctx} className={`site-btn site-btn--${i === 0 && !(syncFirst && r.collection_source) ? cardWeight : NEXT_WEIGHT[cardWeight]} site-btn--sm`} />)}
                    {cardSync && !syncFirst && r.collection_source && <SyncButton collectionId={r.id} ctx={ctx} label={cardSync.label} weight={buttons.length ? NEXT_WEIGHT[cardWeight] : cardWeight} small />}
                  </div>
                )}
                {fav && <Heart row={r} ctx={ctx} className="site-card-heart" />}
              </div>
            </article>
          );
        })}
      </div>
      )}
      {more > 0 && <p className="site-cards-more">and {more} more on the site</p>}
      {hidden > 0 && viewType !== 'table' && viewType !== 'map' && viewType !== 'marquee' && viewType !== 'calendar' && (
        <div className={`site-cards-showmore site-cards-showmore--${align}`}>
          <button type="button" className="site-btn site-btn--outline site-btn--sm" onClick={() => setLimit((l) => l + m.max)}>
            Show {Math.min(m.max, hidden)} more
          </button>
          <span>{shown.length} of {matched.length}</span>
        </div>
      )}
      {/* On a phone the section's own buttons ("All upcoming events") follow the list instead of taking a
          line of the controls above it; the stylesheet shows one place or the other by width. */}
      {!written && tools.length > 0 && !ctx.present && (
        <div className={`site-cards-after site-cards-after--${align}`}><Buttons buttons={tools} firstStyle={firstStyle} ctx={ctx} small /></div>
      )}
    </div>
  );
}

/**
 * Browse — the app's Browse view on a site: a tab per grouping (`groupings`, else `filters`), a card per
 * value with how many rows carry it and, when the value is an item the site holds (a style, a venue),
 * that item's picture; opening one shows its rows, drawn as `browseItems`, with the other filters still
 * offered and a way back. Explore-by-category for a visitor who does not yet know what to search for.
 */
function Browse({ module, rows, firstStyle, ctx, align }) {
  const m = readFlat(module, MODULE_SCHEMA);
  const groupings = (m.groupings.length ? m.groupings : m.filters).filter((g) => typeof g === 'string' && g);
  const [tab, setTab] = useState(groupings[0] || null);
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const field = groupings.includes(tab) ? tab : groupings[0] || null;
  const values = useMemo(() => (field ? browseValues(rows, field, ctx.rowsById) : []), [rows, field, ctx.rowsById]);
  const picOf = (v) => (v.item ? (v.item.image_cover_url || cf(v.item).image_logo || null) : null);
  const anyPic = values.some(picOf);
  const type = ctx.typesById?.get(rows[0]?.item_type_id);
  const word = (n) => (n === 1 ? (type?.display_singular || 'item') : (type?.display || 'items')).toLowerCase();
  const go = (value) => {
    setOpen(value);
    const top = ref.current?.getBoundingClientRect().top;
    if (typeof top === 'number' && top < 0) ref.current.scrollIntoView({ block: 'start' });
  };
  const items = { ...module, viewType: m.browseItems, views: [], groupings: [] };
  if (!field) return <Cards module={items} rows={rows} firstStyle={firstStyle} ctx={ctx} align={align} />;
  if (open !== null) {
    const subset = applyPicks(rows, { [field]: [open] }, ctx.rowsById);
    const item = values.find((v) => v.value === open)?.item || null;
    return (
      <div className="site-module-body site-browse" ref={ref}>
        <div className="site-browse-head">
          <button type="button" className="site-browse-back" onClick={() => setOpen(null)}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {m.fieldLabels[field] || fieldLabel(field, ctx.fieldNames)}
          </button>
          <div className="site-browse-title">{open}<span>{subset.length} {word(subset.length)}</span></div>
          {item && ctx.reach.has(item.id) && <SiteLink link={{ label: `About ${open}`, itemId: item.id }} ctx={ctx} className="site-btn site-btn--text site-btn--sm" />}
        </div>
        <Cards module={{ ...items, filters: m.filters.filter((f) => f !== field) }} rows={subset} firstStyle={firstStyle} ctx={ctx} align={align} />
      </div>
    );
  }
  return (
    <div className="site-module-body site-browse" ref={ref}>
      {groupings.length > 1 && (
        <div className="site-browse-tabs" role="tablist" aria-label="Browse by">
          {groupings.map((g) => (
            <button key={g} type="button" role="tab" aria-selected={g === field} className={`site-browse-tab${g === field ? ' is-on' : ''}`}
              onClick={() => setTab(g)}>{m.fieldLabels[g] || fieldLabel(g, ctx.fieldNames)}</button>
          ))}
        </div>
      )}
      <div className="site-browse-grid" role="tabpanel">
        {values.map((v) => {
          const pic = picOf(v);
          // When any value has a picture, every card keeps the place for one, so a row of cards lines up;
          // a value without one shows its first letter there.
          return (
            <button key={v.value} type="button" className={`site-browse-card${anyPic ? ' has-pic' : ''}`} onClick={() => go(v.value)}>
              {anyPic && <span className="site-browse-pic">{pic ? <img src={pic} alt="" loading="lazy" /> : <span className="site-browse-letter" aria-hidden="true">{v.value.charAt(0)}</span>}</span>}
              <span className="site-browse-word">{v.value}</span>
              <span className="site-browse-count">{v.count} {word(v.count)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A collection on a month calendar: the Calendar View Type outside the hub (src/views/calendar), loaded on
 * first use, drawing every row the search and filters keep. An item's link opens its page on the site.
 */
function SiteCalendar({ rows, ctx }) {
  const hrefOf = useCallback((r) => (ctx.reach.has(r.id) ? itemPath(ctx.spaceSlug, r, ctx.homeId, ctx) : r.url || null), [ctx]);
  const onOpen = useCallback((r, e) => {
    if (!ctx.reach.has(r.id) || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    ctx.navigate(itemPath(ctx.spaceSlug, r, ctx.homeId, ctx));
  }, [ctx]);
  return (
    <Suspense fallback={<div className="site-map-loading" aria-busy="true">Loading the calendar…</div>}>
      <SiteCalendarView rows={rows} hrefOf={hrefOf} onOpen={onOpen} color="var(--site-primary-fill)" />
    </Suspense>
  );
}

/**
 * A collection on a map: the Map View Type's canvas (src/views/map/MapView.jsx), loaded on first use,
 * drawing every row the search and filters keep. Its settings are the collection's stored Map View's —
 * `locationVia` places a program at the organization that offers it — so the map a collection has in
 * the hub is the one the site shows. A pin's link opens the item's page on the site.
 */
function SiteMap({ module, rows, ctx }) {
  const collId = module.source?.collection === '$context' ? ctx.contextItem?.id : module.source?.collection;
  const coll = collId ? ctx.rowsById.get(collId) : null;
  const settings = useMemo(() => storedViewSettings(coll, 'map', module.source?.view || null), [coll, module.source?.view]);
  const hrefOf = useCallback((r) => (ctx.reach.has(r.id) ? itemPath(ctx.spaceSlug, r, ctx.homeId, ctx) : r.url || null), [ctx]);
  const onOpen = useCallback((r, e) => {
    if (!ctx.reach.has(r.id)) return;   // an outside address opens as a link does
    e.preventDefault();
    ctx.navigate(itemPath(ctx.spaceSlug, r, ctx.homeId, ctx));
  }, [ctx]);
  return (
    <div className="site-map">
      <Suspense fallback={<div className="site-map-loading" aria-busy="true">Loading the map…</div>}>
        <SiteMapView rows={rows} rowsById={ctx.rowsById} settings={settings} hrefOf={hrefOf} onOpen={onOpen}
          color="var(--site-primary-fill)" height={ctx.present ? 420 : 520} />
      </Suspense>
    </div>
  );
}

// A table column's header and width. Built-ins have their own words; any other field takes its name.
const TABLE_HEAD = { title: 'Name', url: 'Link', description: 'Description', date_start: 'Date', date_end: 'Until', type_label: 'Type', location_label: 'Location' };
// Minimums wide enough that a column reads before it is cut; past them every column but a link or a date
// shares the spare room, and below them the table scrolls sideways.
const TABLE_WIDTH = { title: [280, '2fr'], description: [340, '3fr'], url: [150, null], date_start: [140, null], date_end: [140, null] };
const columnTrack = (key) => {
  const [min, grow] = TABLE_WIDTH[key] || [190, '1fr'];
  return { min, track: grow ? `minmax(${min}px, ${grow})` : `${min}px` };
};
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return 'Open'; } };

/**
 * A cell's words on one line, cut once at the end with "…" and shown whole on hover. One element per
 * cell on purpose: when each linked title was its own element, each was cut separately ("Ca…, Ne…").
 */
const CellLine = ({ full, children }) => <span className="site-table-line" title={full || undefined}>{children}</span>;

/** One cell of a table row: the field's value as a visitor reads it, links where the value leads somewhere. */
function TableCell({ field, row, ctx }) {
  if (field === 'title') {
    if (ctx.reach.has(row.id)) return <SiteLink link={{ label: row.title || 'Untitled', itemId: row.id }} ctx={ctx} className="site-table-title" />;
    if (row.url) return <a className="site-table-title" href={row.url} target="_blank" rel="noopener noreferrer">{row.title || 'Untitled'}</a>;
    return <span className="site-table-title">{row.title || 'Untitled'}</span>;
  }
  if (field === 'type_label') return ctx.typesById.get(row.item_type_id)?.display_singular || null;
  if (field === 'date_start' || field === 'date_end') return row[field] ? <CellLine>{formatDate(row[field])}</CellLine> : null;
  const v = field in row && field !== 'custom_fields' ? row[field] : cf(row)[field];
  if (typeof v === 'string' && /^https?:\/\//.test(v)) return <a href={v} target="_blank" rel="noopener noreferrer">{hostOf(v)} ↗</a>;
  const ids = linkedIds(v);
  if (ids) {
    const linked = ids.map((id) => ctx.rowsById.get(id)).filter(Boolean);
    if (!linked.length) return null;
    return (
      <CellLine full={linked.map((r) => r.title).join(', ')}>
        {linked.map((r, i) => (
          <Fragment key={r.id}>{i > 0 ? ', ' : ''}{ctx.reach.has(r.id) ? <SiteLink link={{ label: r.title, itemId: r.id }} ctx={ctx} /> : r.title}</Fragment>
        ))}
      </CellLine>
    );
  }
  if (Array.isArray(v)) {
    const vals = valuesOf(v);
    return vals.length ? <span className="site-table-chips" title={vals.join(', ')}>{vals.map((x) => <span key={x}>{x}</span>)}</span> : null;
  }
  if (v == null || v === '') return null;
  const words = labelMentions(String(v));
  return <CellLine full={words}>{words}</CellLine>;
}

/**
 * A read-only grid that scrolls inside itself: a sticky header row, the name column held at the left
 * while the rest scrolls sideways, and only the rows in view drawn, so a list of a thousand groups costs
 * what a screenful does. The Airtable-embed look, drawn by the site rather than the app's grid, which
 * needs the whole app (its graph, open-item and access state) around it. `capped` is Present's first rows,
 * shown flat.
 */
function SiteTable({ module, rows, capped, ctx }) {
  const cols = tableColumns(module);
  const tracks = cols.map(columnTrack);
  const scrollRef = useRef(null);
  const rowHeight = 46;
  const virtual = useVirtualizer({ count: rows.length, getScrollElement: () => scrollRef.current, estimateSize: () => rowHeight, overscan: 10 });
  const items = capped ? rows.map((r, index) => ({ index, start: index * rowHeight })) : virtual.getVirtualItems();
  const style = { '--site-table-cols': tracks.map((t) => t.track).join(' '), minWidth: `${tracks.reduce((n, t) => n + t.min, 0)}px` };
  return (
    <div className="site-table" role="table" aria-rowcount={rows.length + 1}>
      <div className={`site-table-scroll${capped ? ' is-capped' : ''}`} ref={scrollRef}>
        <div className="site-table-inner" style={style}>
          <div className="site-table-head" role="row">
            {cols.map((c) => <div key={c} role="columnheader" className="site-table-cell">{TABLE_HEAD[c] || fieldLabel(c, ctx.fieldNames)}</div>)}
          </div>
          <div className="site-table-body" style={{ height: `${(capped ? rows.length : 0) * rowHeight || virtual.getTotalSize()}px` }}>
            {items.map((it) => {
              const r = rows[it.index];
              return (
                <div key={r.id} role="row" className="site-table-row" style={{ transform: `translateY(${it.start}px)`, height: `${rowHeight}px` }}>
                  {cols.map((c) => <div key={c} role="cell" className={`site-table-cell site-table-cell--${c === 'title' ? 'title' : 'value'}`}><TableCell field={c} row={r} ctx={ctx} /></div>)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {!capped && <div className="site-table-foot">{rows.length === 1 ? '1 row' : `${rows.length} rows`}</div>}
    </div>
  );
}

/**
 * Custom HTML, fenced. A sandboxed frame that may run scripts but shares no origin with the site,
 * so it never sees the visitor's session or the page around it. It reads only the rows of the
 * collection it names, as JSON, and its only ways out are two messages: its own height, and
 * `arcosGo(path)` for a link inside this site.
 *
 * On a type's page it also reads the item being drawn, as `ARCOS.item` — the same `$context` the
 * other modules read — so one custom section can draw every Tag's page or every set's. It is the
 * item's public face only (title, slug, address, picture, description, type), and null on any other
 * page, so a section written before it reads exactly as it did (2026-10-13).
 */
/** The item a type's page is drawing, as a custom section sees it; null anywhere else. */
function contextOf(ctx) {
  const it = ctx.contextItem;
  if (!it) return null;
  return {
    id: it.id, title: it.title || '', slug: it.slug || null, url: it.url || null,
    href: itemPath(ctx.spaceSlug, it, ctx.homeId, ctx),
    image: cf(it).image_logo || it.image_cover_url || null,
    description: it.description || null,
    type: ctx.typesById?.get(it.item_type_id)?.key || null,
  };
}

function HtmlFrame({ module, rows, ctx }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(320);
  const doc = useMemo(() => {
    const data = JSON.stringify({ rows: rows.map((r) => ({
      title: r.title, url: r.url || null,
      href: ctx.reach.has(r.id) ? itemPath(ctx.spaceSlug, r, ctx.homeId, ctx) : null,
      image: cf(r).image_logo || r.image_cover_url || null,
    })), item: contextOf(ctx) }).replace(/</g, '\\u003c');
    return '<!doctype html><html><head><meta charset="utf-8">'
      + `<script>window.ARCOS = ${data}; window.arcosGo = (p) => parent.postMessage({ arcosGo: p }, '*');`
      // The content's own height. `scrollHeight` is at least the frame's window, so it grew by the
      // parent's margin on every report — 616px, 620, 624… — until the reports stopped.
      + 'const send = () => { const px = Math.ceil(document.documentElement.getBoundingClientRect().height); if (px > 0) parent.postMessage({ arcosHeight: px }, "*"); };'
      + 'addEventListener("load", () => { send(); setTimeout(send, 300); setTimeout(send, 1200); if (document.fonts) document.fonts.ready.then(send); new ResizeObserver(send).observe(document.body); });</script>'
      + `</head><body style="margin:0">${String(module.html || '')}</body></html>`;
  }, [module.html, rows, ctx]);
  const { navigate, base } = ctx;
  useEffect(() => {
    const onMessage = (ev) => {
      if (!ref.current || ev.source !== ref.current.contentWindow) return;   // only this frame is heard
      const d = ev.data;
      if (!d || typeof d !== 'object') return;
      if (typeof d.arcosHeight === 'number' && d.arcosHeight >= 20) setHeight(Math.min(4000, d.arcosHeight + 4));
      if (typeof d.arcosGo === 'string' && (d.arcosGo === base || d.arcosGo.startsWith(base + '/'))) navigate(d.arcosGo);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate, base]);
  return <iframe ref={ref} className="site-html-frame" title="Custom section" sandbox="allow-scripts allow-popups" srcDoc={doc} style={{ height }} />;
}

/**
 * In the site editor's preview, a section a visitor would never see — a collection with nothing in it,
 * a form with none chosen, a dashboard-only kind — still takes a thin band saying why, so it can be
 * found and selected. Visitors never get this; only the editor passes `ctx.editing`.
 */
function EditorOnlyBand({ module, ctx, why }) {
  const selected = ctx.editing.selectedId === module.id;
  return (
    <section data-module-id={module.id} className={`site-band site-band--editable site-band--editor-note${selected ? ' is-selected' : ''}`}
      onClickCapture={(ev) => { ev.preventDefault(); ev.stopPropagation(); ctx.editing.onSelect(module.id); }}>
      <div className="site-band-inner"><p>{why}</p></div>
    </section>
  );
}

/** One module, drawn as a section. Returns null for a module with nothing to show. */
function Section({ module: stored, isFirst, ctx }) {
  // On a type's page (`detail.page_by_type`) the item being drawn is `ctx.contextItem`: a module naming
  // `item: '$context'` reads it, a Relationship collection walks from it, and `{field}` words fill from it.
  const context = ctx.contextItem || null;
  const module = context && stored.item === '$context' ? { ...stored, item: context.id } : stored;
  const s = readFlat(module.section, SECTION_SCHEMA);
  const head = headOf(module, ctx.rowsById);
  if (context) {
    for (const k of ['eyebrow', 'title', 'body']) head[k] = head[k] ? fillTokens(head[k], context, ctx.rowsById) || null : head[k];
    head.buttons = head.buttons.map((b) => ({ ...b, label: fillTokens(b.label, context, ctx.rowsById) }));
  }
  // A button that says a live number: `{count}` in its words is what its `count` counts, as stats do.
  head.buttons = head.buttons.map((b) => {
    if (!b.count || !String(b.label).includes('{count}')) return b;
    const n = statValue(b.count, ctx);
    return { ...b, label: n == null ? b.label.replace(/\s*\{count\}/, '') : b.label.replace('{count}', n.toLocaleString()) };
  });
  const editing = ctx.editing || null;
  const contextId = context?.id || null;
  // In the editor, a click anywhere in a section selects it rather than following what is under it.
  const editProps = editing ? {
    'data-module-id': module.id,
    onClickCapture: (ev) => { ev.preventDefault(); ev.stopPropagation(); editing.onSelect(module.id); },
  } : {};
  const editCls = editing ? ` site-band--editable${editing.selectedId === module.id ? ' is-selected' : ''}` : '';
  let body = null;
  let listBand = false;
  let tableBand = false;
  if (module.archetype === 'fields') {
    // The item's own fields, as an item page draws them, wherever the section sits on the page.
    if (!context) return editing ? <EditorOnlyBand module={module} ctx={ctx} why="Shows the fields of the item this page draws. Open one of those items in the preview to see it." /> : null;
    const m = readFlat(module, MODULE_SCHEMA);
    return (
      <div id={module.anchor || module.id} className={`site-fields-band${editCls}`} {...editProps}>
        <ItemPage row={context} ctx={ctx} fields={m.fields.length ? m.fields : null} layout={m.layout === 'auto' ? null : m.layout} />
      </div>
    );
  }
  if (module.archetype === 'collection') {
    // `$context`: on a type's page, the item being drawn is itself the collection shown (a calendar's events).
    const collId = module.source?.collection === '$context' ? contextId : module.source?.collection;
    const coll = collId ? ctx.rowsById.get(collId) : null;
    const found = coll ? resolveSiteCollection(coll, ctx.rows, ctx.typesById, { contextId }) : [];
    // `sortBy: { field: '$count' }` orders by the number each card shows (`cardCount`), most first.
    const byCount = module.source?.sortBy?.field === '$count' && module.cardCount?.collection;
    const rows = byCount
      ? found.map((r, i) => ({ r, i, n: cardCountOf(r, module.cardCount, ctx) || 0 }))
        .sort((a, b) => (module.source.sortBy.direction === 'asc' ? a.n - b.n : b.n - a.n) || a.i - b.i).map((x) => x.r)
      : sortSiteRows(found, module.source?.sortBy);
    // `{count}` in a collection's words is how many it holds: "51 socials, classes and parties this week".
    for (const k of ['eyebrow', 'title', 'body']) if (head[k] && head[k].includes('{count}')) head[k] = head[k].replace(/\{count\}/g, rows.length.toLocaleString());
    if (!rows.length) {   // a module with nothing to show hides its whole band
      if (!editing) return null;
      const why = coll ? `“${coll.title}” has nothing to show yet, so visitors don’t see this section.`
        : 'Choose a collection for this section. Visitors don’t see it until it has items.';
      return <EditorOnlyBand module={module} ctx={ctx} why={why} />;
    }
    // A collection with a search box or filters draws its own buttons at the end of that row, so the
    // list's controls are one line; without either, they stay under the heading as on any section.
    const m = readFlat(module, MODULE_SCHEMA);
    const tools = m.viewType !== 'browse' && (m.search || m.filters.length) ? head.buttons : [];
    if (tools.length) head.buttons = [];
    // A feed spans the page's width, so its days line up with the heading and the sections around it.
    listBand = m.viewType === 'list' || m.viewType === 'table';
    tableBand = m.viewType === 'table';
    body = m.viewType === 'browse'
      ? <Browse module={module} rows={rows} firstStyle={s.button_style} ctx={ctx} align={s.align} />
      : <Cards module={module} rows={rows} firstStyle={s.button_style} ctx={ctx} tools={tools} align={s.align} />;
  } else if (module.archetype === 'info') {
    if (Array.isArray(module.cards) && module.cards.length) body = <Cards module={module} rows={module.cards} written firstStyle={s.button_style} ctx={ctx} />;
  } else if (module.archetype === 'form') {
    if (!module.form) return editing ? <EditorOnlyBand module={module} ctx={ctx} why="A form section with no form chosen. Visitors don’t see it." /> : null;
    head.buttons = [{ label: module.formLabel || 'Open the form', href: `/form/${module.form}` }, ...head.buttons];
  } else if (module.archetype === 'html') {
    const coll = module.source?.collection ? ctx.rowsById.get(module.source.collection) : null;
    body = <HtmlFrame module={module} rows={coll ? sortSiteRows(resolveSiteCollection(coll, ctx.rows, ctx.typesById, { contextId }), module.source?.sortBy) : []} ctx={ctx} />;
  } else if (module.archetype !== 'header') {
    // a dashboard-only kind (ask, chat, deck…) has no public form
    return editing ? <EditorOnlyBand module={module} ctx={ctx} why="This module shows only in Arcos, never on the website." /> : null;
  }

  const hasText = head.eyebrow || head.title || head.body || head.buttons.length;
  const ov = s.overlay === 'auto' ? (head.background ? 'dark' : 'none') : s.overlay;
  const style = {};
  if (head.background) {
    const tone = { primary: 'var(--site-primary-fill)', accent: 'var(--site-accent-fill)', inverse: 'var(--site-ink)' }[s.tone]
      || (ov === 'light' ? 'var(--site-surface)' : 'var(--site-ink)');
    const amount = ov === 'light' ? '80%' : (s.tone === 'plain' || s.tone === 'muted') ? '66%' : '77%';
    const fadeTo = { fade: 'to bottom', fade_bottom: 'to top' }[ov];
    const veil = ov === 'none' ? ''
      : ov === 'gradient' ? 'linear-gradient(135deg, color-mix(in srgb, var(--site-primary-fill) 88%, transparent) 0%, color-mix(in srgb, var(--site-primary-fill) 70%, transparent) 45%, color-mix(in srgb, var(--site-accent-fill) 55%, transparent) 100%), '
      : fadeTo ? `linear-gradient(${fadeTo}, var(--site-surface) 0%, color-mix(in srgb, var(--site-surface) 72%, transparent) 42%, transparent 80%), `
      : `linear-gradient(color-mix(in srgb, ${tone} ${amount}, transparent), color-mix(in srgb, ${tone} ${amount}, transparent)), `;
    style.backgroundImage = `${veil}url(${JSON.stringify(head.background)})`;
  }
  const text = hasText ? (
    <div className="site-text">
      {head.badge && (
        <SiteLink link={head.badge.itemId || head.badge.href ? head.badge : { ...head.badge, href: '#' }} ctx={ctx} className="site-pill site-badge">
          {head.badge.image && <span className="site-pill-icon has-logo"><img src={head.badge.image} alt="" /></span>}
          <span className="site-pill-word">{head.badge.label}</span>
        </SiteLink>
      )}
      {head.eyebrow && <div className="site-eyebrow">{head.eyebrow}</div>}
      {head.title && (isFirst
        ? <h1 className={`site-title site-title--${s.title_size}`}>{head.title}</h1>
        : <h2 className={`site-title site-title--${s.title_size}`}>{head.title}</h2>)}
      {head.body && <Markdown className="site-body" text={head.body} />}
      <Buttons buttons={head.buttons} firstStyle={s.button_style} ctx={ctx} />
    </div>
  ) : null;
  // A live picture (`media`) stands where a section's image would: `kind: 'week'` draws the next days of a
  // collection's real events arriving in a calendar.
  const live = module.media && typeof module.media === 'object' && module.media.kind === 'week' && module.media.collection
    ? ctx.rowsById.get(module.media.collection) : null;
  const liveRows = live ? sortSiteRows(resolveSiteCollection(live, ctx.rows, ctx.typesById, { contextId }), { field: 'Date Start' }) : null;
  const media = head.aside
    ? <Markdown className="site-aside" text={head.aside} />
    : liveRows && liveRows.length
      ? <div className="site-media site-media--art"><SiteWeekArt rows={liveRows} ctx={ctx} id={module.id} label={module.media.label || 'Your calendar'}
          from={module.media.from || ctx.siteTitle} field={module.media.field || null} /></div>
      : head.image ? <div className="site-media"><img src={head.image} alt="" /></div> : null;
  let pos = media ? s.media_position : 'none';
  if (pos === 'opposite' && s.align === 'center') pos = 'above';
  const rowCls = `site-row site-align--${s.align}`;
  const row = !text && !media ? null
    : pos === 'opposite' ? <div className={`${rowCls} site-row--split`}>{s.align === 'right' ? <>{media}{text}</> : <>{text}{media}</>}</div>
    : pos === 'above' ? <div className={rowCls}>{media}{text}</div>
    : pos === 'below' ? <div className={rowCls}>{text}{media}</div>
    : <div className={`${rowCls} site-row--solo`}>{text}</div>;

  return (
    <section
      id={module.anchor || module.id}
      className={`site-band site-tone--${s.tone} site-height--${s.height} site-width--${s.width}${listBand ? ' site-band--list' : ''}${tableBand ? ' site-band--table' : ''}${head.background ? ` site-band--image site-overlay--${ov}` : ''}${editCls}`}
      style={style}
      {...editProps}
    >
      <div className="site-band-inner">{row}{Array.isArray(module.stats) && module.stats.length > 0 && <Stats stats={module.stats} ctx={ctx} />}{body}</div>
    </section>
  );
}

const CONTACT_HREF = {
  phones: (v) => `tel:${v.replace(/[^\d+]/g, '')}`,
  emails: (v) => `mailto:${v}`,
};

/** One labelled fact on an item's page: a field's name, then its value. */
const Fact = ({ label, children }) => (
  <div className="site-fact"><div className="site-fact-label">{label}</div><div className="site-fact-value">{children}</div></div>
);

// Item columns a page may list beside its custom fields, read from the row itself, and their labels.
const ROW_DATES = { date_start: 'Date', date_end: 'Until' };
const URL_LABEL = { url: 'Website', volunteer_url: 'Volunteer', donate_url: 'Donate', petition_url: 'Sign Petition', pdf_url: 'Read the PDF' };
// A written answer this long, or one with a line break, is read as prose under its own heading rather
// than squeezed into a fact.
const LONG_TEXT = 240;

/**
 * An item's fields, in the order the site lists them, each placed in a slot:
 *   head    the eyebrow, title, lede, logo and tags — what the page opens with
 *   media   the cover picture or the video
 *   body    the page itself, and any long written answer under its field's name
 *   gallery a field holding several pictures
 *   fact    one labelled short answer; a linked item is a link to its own page
 *   buttons the item's own `links`, and every web address that is not a video
 *   contents the body's table of contents, when detail.contents_by_type asks and the body is long
 * The article layout draws them in order, the contents beside them; the case study arranges them by
 * slot and has no contents.
 */
function itemPieces(row, ctx, only = null) {
  const type = ctx.typesById.get(row.item_type_id);
  const fields = only || ctx.settings.detail.fields_by_type?.[type?.key] || ['title', 'description', 'page_markdown', 'url'];
  const custom = cf(row);
  const names = ctx.fieldNames || {};
  const out = [];
  const urlButtons = [];
  const put = (slot, key, el) => out.push({ slot, key, el });
  for (const f of fields) {
    if (f === 'type_label') { if (type?.display_singular) put('head', f, <div key={f} className="site-article-eyebrow">{type.display_singular}</div>); continue; }
    if (f === 'title') {
      put('head', f, ctx.settings.detail.favorites && ctx.fav
        ? <div key={f} className="site-article-titlerow"><h1>{row.title}</h1><Heart row={row} ctx={ctx} /></div>
        : <h1 key={f}>{row.title}</h1>);
      continue;
    }
    if (f === 'description') { if (row.description) put('head', f, <p key={f} className="site-article-lede">{labelMentions(row.description)}</p>); continue; }
    if (f === 'page_markdown') {
      if (!row.page_markdown) continue;
      if (!ctx.settings.detail.contents_by_type?.[type?.key]) { put('body', f, <Markdown key={f} className="site-article-body" text={row.page_markdown} />); continue; }
      // Every heading gets its address whether or not the body is long enough for a contents list.
      const { html, outline } = outlineHtml(siteMarkdown(row.page_markdown));
      const tree = outlineTree(outline);
      if (tree.reduce((n, t) => n + (t.id ? 1 : 0) + t.children.length, 0) >= CONTENTS_MIN) put('contents', f, <Contents key="contents" tree={tree} ctx={ctx} />);
      put('body', f, <Markdown key={f} className="site-article-body" html={html} />);
      continue;
    }
    if (f === 'image_logo') { if (custom.image_logo) put('head', f, <img key={f} className="site-article-img" src={custom.image_logo} alt="" />); continue; }
    if (f === 'image_cover_url') { if (row.image_cover_url) put('media', f, <img key={f} className="site-article-cover" src={row.image_cover_url} alt="" />); continue; }
    if (f === 'tags') { const tags = Array.isArray(custom.tags) ? custom.tags : []; if (tags.length) put('head', f, <div key={f} className="site-article-tags">{tags.map((t) => <span key={t}>{t}</span>)}</div>); continue; }
    if (f === 'links') { const links = Array.isArray(custom.links) ? custom.links : []; if (links.length) put('buttons', f, <Buttons key={f} buttons={links} firstStyle="filled" ctx={ctx} />); continue; }
    if (names[f] === null) continue;   // a retired field is not offered, so not shown
    const label = ctx.settings.detail.field_labels?.[f] || fieldLabel(f, names);
    // A start with a time reads on the visitor's clock ("Tue, Oct 14 · 8:00 PM"); a day-only one as its day.
    if (ROW_DATES[f]) { if (row[f]) put('fact', f, <Fact key={f} label={ROW_DATES[f]}>{timeOf(row[f]) ? whenOf(row[f]) : formatDate(row[f])}</Fact>); continue; }
    if (f === 'youtube_video_id') {   // a bare id, not an address
      const video = videoOf({ custom_fields: { youtube_video_id: custom[f] } });
      if (video) put('media', f, <VideoPlayer key={f} video={video} title={row.title} className="site-article-video" />);
      continue;
    }
    const v = f === 'url' ? row.url : custom[f];
    if (typeof v === 'string' && /^(https?:|mailto:)/.test(v)) {
      const video = youtubeOf(v);
      if (video) put('media', f, <VideoPlayer key={f} video={video} title={row.title} className="site-article-video" />);
      else {
        // A site's own words win; `{domain}` in them is the address's site ("More at danceus.org").
        const own = ctx.settings.detail.field_labels?.[f];
        const domain = (() => { try { return new URL(v).hostname.replace(/^www\./, ''); } catch { return ''; } })();
        const words = own ? own.replace('{domain}', domain).trim()
          : f === 'video_url' ? (/spotify\.com/.test(v) ? 'Listen' : 'Watch') : (URL_LABEL[f] || label);
        urlButtons.push({ label: words, href: v, fileTitle: `${row.title} · ${words}` });
      }
      continue;
    }
    const ids = linkedIds(v);
    if (ids) {
      const linked = ids.map((id) => ctx.rowsById.get(id)).filter(Boolean);
      if (linked.length) {
        put('fact', f, (
          <Fact key={f} label={label}>
            {linked.map((r) => <ItemPill key={r.id} row={r} ctx={ctx} />)}
          </Fact>
        ));
      }
      continue;
    }
    if (Array.isArray(v) && v.length && v.every(isImageUrl)) {
      put('gallery', f, <div key={f} className="site-gallery">{v.map((src) => <img key={src} src={src} alt="" loading="lazy" />)}</div>);
      continue;
    }
    if (typeof v === 'string' && (v.length > LONG_TEXT || v.includes('\n'))) {
      put('body', f, <section key={f} className="site-article-section"><h2>{label}</h2><Markdown className="site-article-body" text={v} /></section>);
      continue;
    }
    const vals = valuesOf(v);
    if (!vals.length) continue;
    if (CONTACT_HREF[f]) {
      put('fact', f, <Fact key={f} label={label}>{vals.map((x) => <a key={x} href={CONTACT_HREF[f](x)}>{x}</a>)}</Fact>);
    } else if (Array.isArray(v)) {
      put('fact', f, <Fact key={f} label={label}><div className="site-article-tags">{vals.map((x) => <span key={x}>{x}</span>)}</div></Fact>);
    } else {
      put('fact', f, <Fact key={f} label={label}>{labelMentions(vals[0])}</Fact>);
    }
  }
  if (urlButtons.length) put('buttons', 'url-buttons', <Buttons key="url-buttons" buttons={urlButtons} firstStyle="filled" ctx={ctx} />);
  return out;
}

/** The reached items that point at this one, by type — a project's talks, an organization's projects. */
function Related({ row, ctx }) {
  const groups = backlinksOf(row, ctx);
  if (!groups.length) return null;
  return (
    <section className="site-related">
      {groups.map((g) => (
        <div key={g.type?.id || 'untyped'} className="site-related-group">
          <h2>{g.type?.display || 'Related'}</h2>
          <div className="site-related-list">
            {g.rows.map((r) => {
              const video = videoOf(r);
              const src = r.image_cover_url || (video ? youtubeThumb(video.id) : cf(r).image_logo) || null;
              return (
                <SiteLink key={r.id} link={{ label: r.title, itemId: r.id }} ctx={ctx} className="site-related-item">
                  {src ? <img src={src} alt="" loading="lazy" /> : <span className="site-related-blank" aria-hidden="true" />}
                  <span>{r.title}</span>
                </SiteLink>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * An item that is not a page, rendered from its own fields as the site's settings choose — one column
 * in order (`article`), or the `case_study` arrangement: the cover or video across the top, the body
 * and long answers on the left, the facts and buttons in a column on the right.
 */
function ItemPage({ row, ctx, fields = null, layout: asked = null }) {
  const type = ctx.typesById.get(row.item_type_id);
  const layout = (asked || ctx.settings.detail.layout_by_type?.[type?.key]) === 'case_study' ? 'case_study' : 'article';
  const pieces = itemPieces(row, ctx, fields);
  const related = ctx.settings.detail.show_related ? <Related row={row} ctx={ctx} /> : null;
  if (layout === 'article') {
    const contents = pieces.find((p) => p.slot === 'contents');
    const article = <article className="site-article site-article--narrow">{pieces.filter((p) => p !== contents).map((p) => p.el)}{related}</article>;
    return contents ? <div className="site-doc"><aside className="site-doc-toc">{contents.el}</aside>{article}</div> : article;
  }
  const of = (...slots) => pieces.filter((p) => slots.includes(p.slot)).map((p) => p.el);
  // The first picture or video opens the page; any other plays in the body, above the gallery.
  const media = pieces.filter((p) => p.slot === 'media');
  const rail = of('fact', 'buttons');
  // Nothing for the main column — no body, no gallery, no second video: the facts follow the head in one
  // column, rather than sitting in a rail beside an empty page.
  const mainEmpty = !of('body').length && !of('gallery').length && media.length < 2 && !related;
  if (mainEmpty) {
    return (
      <article className="site-case site-case--short">
        <header className="site-case-head">{of('head')}</header>
        {media[0] && <div className="site-case-hero">{media[0].el}</div>}
        {rail.length > 0 && <div className="site-case-facts">{rail}</div>}
      </article>
    );
  }
  return (
    <article className="site-case">
      <header className="site-case-head">{of('head')}</header>
      {media[0] && <div className="site-case-hero">{media[0].el}</div>}
      <div className={`site-case-grid${rail.length ? '' : ' site-case-grid--solo'}`}>
        <div className="site-case-main">
          {of('body')}
          {media.slice(1).map((p) => p.el)}
          {of('gallery')}
        </div>
        {rail.length > 0 && <aside className="site-case-rail">{rail}</aside>}
      </div>
      {related}
    </article>
  );
}

function SiteHeader({ siteRow, ctx, currentId, show }) {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  // The phone menu closes itself. The header outlives a page change (navigation is a pushState), so
  // without this the menu stayed open over the new page until Menu was tapped again. A tap outside the
  // header, Escape, or a page change closes it; a tap on one of its links closes it as it navigates.
  useEffect(() => { setOpen(false); }, [currentId]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!show) return null;
  const s = cf(siteRow);
  const nav = idList(s.header_links).map((id) => ctx.rowsById.get(id)).filter(Boolean);
  const label = (r) => cf(r).nav_label || r.title;
  return (
    <header ref={headerRef} className={`site-header site-header--${ctx.settings.chrome.header_style}${ctx.settings.chrome.sticky_header ? ' site-header--sticky' : ''}`}>
      <div className="site-header-inner">
        <a className="site-brand" href={ctx.base || '/'}>
          {s.image_logo && <img src={s.image_logo} alt="" />}
          <span>{siteRow.title}</span>
        </a>
        <button type="button" className="site-nav-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>Menu</button>
        <ul className={`site-nav${open ? ' is-open' : ''}`} onClick={(e) => { if (e.target.closest('a')) setOpen(false); }}>
          {nav.map((r) => {
            const menu = idList(cf(r).menu_links).map((id) => ctx.rowsById.get(id)).filter(Boolean);
            // A page reached through this entry's menu marks the entry too, so a sub-page still says
            // which section of the site it belongs to.
            const inSection = r.id === currentId || menu.some((mr) => mr.id === currentId);
            return (
              <li key={r.id}>
                <a href={itemPath(ctx.spaceSlug, r, ctx.homeId, ctx)} className={inSection ? 'is-current' : undefined}
                  aria-current={r.id === currentId ? 'page' : undefined}>{label(r)}</a>
                {menu.length > 0 && (
                  <ul className="site-menu">
                    {menu.map((mr) => (
                      <li key={mr.id}>
                        <a href={itemPath(ctx.spaceSlug, mr, ctx.homeId, ctx)} className={mr.id === currentId ? 'is-current' : undefined}
                          aria-current={mr.id === currentId ? 'page' : undefined}>{label(mr)}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </header>
  );
}

function SiteFooter({ siteRow, ctx, show }) {
  if (!show) return null;
  const s = cf(siteRow);
  const links = idList(s.footer_links).map((id) => ctx.rowsById.get(id)).filter(Boolean);
  const { footer_tone: tone, footer_logo: logo } = ctx.settings.chrome;
  return (
    <footer className={`site-footer site-footer--${tone}`}>
      <div className="site-footer-inner">
        {s.image_logo && <img className={`site-footer-logo${logo === 'white' ? ' site-footer-logo--white' : ''}`} src={s.image_logo} alt="" />}
        <ul>
          {links.map((r) => (
            <li key={r.id}>
              {r.url && !cf(r).Views
                ? <SiteLink link={{ label: cf(r).nav_label || r.title, href: r.url }} ctx={ctx} />
                : <a href={itemPath(ctx.spaceSlug, r, ctx.homeId, ctx)}>{cf(r).nav_label || r.title}</a>}
            </li>
          ))}
        </ul>
        {/* Every site says what it is built with, on the line that names the site — quiet until pointed at. */}
        <div className="site-footer-base">
          <span className="site-footer-name">{siteRow.title}</span>
          <a className="site-built-with" href="https://www.arcos.network" target="_blank" rel="noopener noreferrer">
            <span>Built with</span>
            <img src={footerIsDark(ctx.settings.theme, tone) ? '/arcos-logo-on-dark.svg' : '/arcos-logo.svg'} alt="Arcos" />
          </a>
        </div>
      </div>
    </footer>
  );
}

function SiteMessage({ title, children }) {
  return (
    <div className="site-root site-message">
      <h1>{title}</h1>
      {children && <p>{children}</p>}
    </div>
  );
}

/**
 * @param {string}  workspaceSlug
 * @param {boolean} embedded  rendered inside the Arcos app shell rather than site.html. The app locks
 *   body scrolling (src/styles/index.css: body overflow hidden, 100dvh), so there the site must be its
 *   OWN scroll container or nothing below the first screen can be reached. On site.html the document
 *   scrolls, which is what phones handle best (collapsing address bar, momentum, pull to refresh).
 */
export default function SiteApp({ workspaceSlug, embedded = false, preload = null, routing = 'window', initialPath = null, onPathChange = null,
  editing = null, onCurrentChange = null, base: domainBase = null }) {
  // The editor hands its own, fresher data in (`preload`) and keeps the preview's moves inside the
  // preview (`routing: 'memory'`), so clicking around the site never changes the editor's address.
  // `editing` ({ selectedId, onSelect }) makes each section selectable, and `onCurrentChange` hears
  // which item the preview is on. Both are the editor's alone.
  const fetched = useSiteData(preload ? null : workspaceSlug);
  const data = preload || fetched;
  const memory = routing === 'memory';
  // On arcos.network a site lives under /<space>/site. On its own domain (www.cooperationli.org) it is
  // the whole domain: site.html is told so (window.__SITE__.base = ''), and every address, link and
  // check below follows the root it is given. A form is the one thing that still lives in the app.
  const onOwnDomain = typeof domainBase === 'string';
  // Every site of the space lives under /<space>/site on arcos.network — the primary there, the others
  // a segment below it (siteModel.siteForPath) — so that one prefix is what the app routes and
  // intercepts. On its own domain the prefix is the domain's root, and the domain serves the primary.
  const spaceBase = onOwnDomain ? domainBase : siteBase(workspaceSlug);
  const spaceHome = spaceBase || '/';
  const rootRef = useRef(null);
  const [location, setLocation] = useState(() => (memory
    ? { path: initialPath || spaceHome, search: '', hash: '' }
    : { path: window.location.pathname, search: window.location.search, hash: window.location.hash }));

  useEffect(() => { if (!memory) dismissHubSplash(); }, [memory]);
  // The editor moves the preview by changing `initialPath`; its own echo of a move made inside the
  // preview changes nothing, so an anchor the preview just jumped to survives.
  useEffect(() => {
    if (memory && initialPath) setLocation((l) => (l.path === initialPath ? l : { path: initialPath, search: '', hash: '' }));
  }, [memory, initialPath]);

  useEffect(() => {
    if (memory) return undefined;
    const onPop = () => setLocation({ path: window.location.pathname, search: window.location.search, hash: window.location.hash });
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [memory]);

  const navigate = useCallback((href) => {
    const url = new URL(href, window.location.origin);
    // `?view=public` (the site exactly as a stranger sees it) survives moving between pages.
    if (!url.search && new URLSearchParams(window.location.search).get('view') === 'public' && !memory) url.search = '?view=public';
    const next = { path: url.pathname, search: url.search, hash: url.hash };
    if (memory) onPathChange?.(url.pathname);
    else window.history.pushState(null, '', url.pathname + url.search + url.hash);
    setLocation(next);
    if (!url.hash) {
      if (embedded || memory) rootRef.current?.scrollTo(0, 0);
      else window.scrollTo(0, 0);
    }
  }, [embedded, memory, onPathChange]);

  const ready = data.status === 'ready';
  const sites = useMemo(() => (ready ? sitesOf(data.rows, data.siteRow?.primary_space_id) : []), [ready, data.rows, data.siteRow]);
  // Which of the space's sites this address is on, and the segments left for that site's own pages.
  const picked = useMemo(() => {
    const segs = location.path.startsWith(spaceBase) ? location.path.slice(spaceBase.length).split('/').filter(Boolean) : [];
    return onOwnDomain ? { site: sites[0] || null, rest: segs } : siteForPath(segs, sites);
  }, [location.path, spaceBase, sites, onOwnDomain]);
  const siteRow = ready ? (picked.site || data.siteRow) : null;
  const base = useMemo(() => (onOwnDomain ? domainBase : siteRow ? siteBaseOf(workspaceSlug, siteRow, sites) : spaceBase),
    [onOwnDomain, domainBase, siteRow, workspaceSlug, sites, spaceBase]);
  const homeHref = base || '/';
  const settings = useMemo(() => readSiteSettings(cf(siteRow)), [siteRow]);
  useThemeFonts(useMemo(() => [settings.theme.font_heading, settings.theme.font_body], [settings]));
  // Remember the site's colour, so a slow load next time draws its fallback line in it.
  useEffect(() => {
    if (!ready) return;
    try { localStorage.setItem(siteColorKey(workspaceSlug), settings.theme.primary); } catch { /* storage may be blocked */ }
  }, [ready, workspaceSlug, settings]);

  // The form open over the page, if any: `{ id, href }`.
  const [formOpen, setFormOpen] = useState(null);
  const openForm = useCallback((id, href) => setFormOpen({ id, href }), []);
  const closeForm = useCallback(() => setFormOpen(null), []);
  // The file open over the page, if any: `{ href, kind, title }`.
  const [fileOpen, setFileOpen] = useState(null);
  const openFile = useCallback((file) => setFileOpen(file), []);
  const closeFile = useCallback(() => setFileOpen(null), []);

  // ── Favorites: the visitor's own saves (siteFavorites.js), read as them only once a heart or the
  // Saved chip asks, and never in the editor's preview, where a click selects a section.
  const favOn = !memory && !editing;
  const [fav, setFav] = useState({ account: null, ids: new Set(), busy: new Set() });
  const [signIn, setSignIn] = useState(null);   // { title, line, after }: the sign-in dialog, open
  const favLoad = useRef(null);   // the read in flight or done, so a tap can wait for it
  const favRef = useRef(fav);
  favRef.current = fav;
  const reachRef = useRef([]);
  const ensureFav = useCallback((force = false) => {
    if (favLoad.current && !force) return favLoad.current;
    favLoad.current = (async () => {
      if (!hasStoredSession()) return null;   // no sign-in in this browser: nothing saved to read
      try {
        const { account, ids } = await (await loadFavoritesApi()).loadFavorites(reachRef.current);
        favRef.current = { ...favRef.current, account, ids };
        setFav((f) => ({ ...f, account, ids }));
        return account;
      } catch { return null; }   // the hearts stay empty; a tap asks again
    })();
    return favLoad.current;
  }, []);
  const askToSignIn = useCallback((ask) => setSignIn({
    title: ask.title || 'Save this to come back to',
    line: ask.line || 'Sign in, or make a free Arcos account, and what you save stays with you on every device.',
    after: ask.after || null,
    mark: ask.mark || 'heart',
  }), []);
  const toggleFav = useCallback((row) => {
    const save = async (on) => {
      setFav((f) => {
        const ids = new Set(f.ids); if (on) ids.add(row.id); else ids.delete(row.id);
        return { ...f, ids, busy: new Set([...f.busy, row.id]) };
      });
      try { await (await loadFavoritesApi()).setFavorite(row, on); }
      catch {   // refused: the heart goes back to what is true
        setFav((f) => { const ids = new Set(f.ids); if (on) ids.delete(row.id); else ids.add(row.id); return { ...f, ids }; });
      }
      setFav((f) => { const busy = new Set(f.busy); busy.delete(row.id); return { ...f, busy }; });
    };
    (async () => {
      // A visitor already signed in is still being read on a first, quick tap: wait for it.
      const account = favRef.current.account || await ensureFav();
      if (!account) {
        const word = (data.typesById?.get(row.item_type_id)?.display_singular || 'item').toLowerCase();
        askToSignIn({ title: `Save this ${word}`, after: () => save(true) });
        return;
      }
      save(!favRef.current.ids.has(row.id));
    })();
  }, [askToSignIn, ensureFav, data.typesById]);
  const onSignedIn = useCallback(async () => {
    const after = signIn?.after;
    setSignIn(null);
    await ensureFav(true);
    if (favRef.current.account) after?.();
  }, [signIn, ensureFav]);
  const favCtx = useMemo(() => (favOn ? { ...fav, ensure: ensureFav, toggle: toggleFav, askToSignIn } : null),
    [favOn, fav, ensureFav, toggleFav, askToSignIn]);

  const ctx = useMemo(() => {
    if (!ready) return null;
    const rowsById = new Map(data.rows.map((r) => [r.id, r]));
    const reach = reachableIds({ siteRow, rows: data.rows, typesById: data.typesById });
    const homeId = idList(cf(siteRow).home_page)[0] || null;
    return { spaceSlug: workspaceSlug, base, sites, siteId: siteRow.id, appOrigin: onOwnDomain ? APP_ORIGIN : null, rows: data.rows, rowsById,
      typesById: data.typesById, fieldNames: data.fieldNames, spaceId: siteRow.primary_space_id, slugBySpace: data.spaces, reach, homeId,
      settings, navigate, editing, openForm, openFile, fav: favCtx, siteTitle: siteRow.title || '',
      // What a Share button hands on (siteModel.siteShareUrl): the site's own domain when it has one.
      shareUrl: siteShareUrl({ ownOrigin: data.origin, isPrimary: !sites.length || sites[0].id === siteRow.id, base,
        locationOrigin: window.location.origin }) };
  }, [ready, data.rows, siteRow, sites, data.typesById, data.fieldNames, data.spaces, data.origin, workspaceSlug, base, onOwnDomain, settings, navigate, editing, openForm, openFile, favCtx]);
  reachRef.current = ctx ? [...ctx.reach] : [];


  // One segment is this site's own item; two are another space's, `<owner>/<slug or id>` (siteModel).
  const segments = picked.rest;
  const current = !ctx ? null : segments.length ? itemForPath(segments, ctx) : ctx.rowsById.get(ctx.homeId);
  const view = current ? pageViewOf(current) : null;
  // An item whose type has a page (`detail.page_by_type`) is drawn by that page, with the item as its
  // `$context`. The type's page itself has no item of its own: in the editor it shows the first item
  // of its type, and a visitor who finds its address is told it is not on the site.
  const templateRow = ctx && current && !view ? templatePageFor(current, settings, ctx.typesById, ctx.rowsById) : null;
  const templateType = ctx && current && view ? templateTypeOf(current, settings) : null;
  const sample = templateType && editing
    ? ctx.rows.find((r) => ctx.typesById.get(r.item_type_id)?.key === templateType && ctx.reach.has(r.id)) || null : null;
  const drawn = templateRow ? pageViewOf(templateRow) : view;
  const contextItem = templateRow ? current : sample;
  const hiddenTemplate = !!templateType && !editing;

  const currentId = current?.id || null;
  useEffect(() => { onCurrentChange?.(currentId); }, [currentId, onCurrentChange]);

  useEffect(() => {
    if (!current || memory) return;
    document.title = pageTitle(current, siteRow, current.id === ctx?.homeId);
  }, [current, ctx, siteRow, memory]);

  // One visit per page for the space's Usage tab. Never from the editor's preview, the sidebar's
  // embedded preview, or `?view=public` (an admin checking what a stranger sees).
  const countVisit = !memory && !embedded && !editing && new URLSearchParams(location.search).get('view') !== 'public';
  useEffect(() => {
    if (countVisit && currentId) recordSiteVisit({ space: workspaceSlug, ref: currentId });
  }, [countVisit, currentId, workspaceSlug]);

  // The edit button waits until the page has drawn, then loads only for a browser holding a sign-in.
  const params = new URLSearchParams(location.search);
  const presenting = !memory && params.has('present');
  const [pillReady, setPillReady] = useState(false);
  useEffect(() => {
    if (memory || !hasStoredSession()) return undefined;
    const t = setTimeout(() => setPillReady(true), 600);
    return () => clearTimeout(t);
  }, [memory]);
  const showPill = pillReady && !presenting && params.get('view') !== 'public';

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'start' }));
  }, [location, current]);

  // Links inside the site move by pushState; everything else behaves as a link.
  const onClick = useCallback((ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const a = ev.target.closest('a');
    if (!a || a.target === '_blank') return;
    const href = a.getAttribute('href') || '';
    // In the site: its root, a path under it, or the root with an anchor. With no root (its own
    // domain) that is any path on the domain — but never a bare `#anchor`, which the browser keeps.
    const inSite = spaceBase
      ? href === spaceBase || href.startsWith(spaceBase + '/') || href.startsWith(spaceBase + '#')
      : href.startsWith('/') && !href.startsWith('//');
    if (inSite) {
      ev.preventDefault();
      navigate(href);
    }
  }, [spaceBase, navigate]);

  if (data.status === 'loading') return <SiteLoading slug={workspaceSlug} />;
  if (data.status === 'error') return <SiteMessage title="This site couldn’t load">Please try again in a moment.</SiteMessage>;
  if (data.status === 'missing') return <SiteMessage title="No site here">This space has not published a website.</SiteMessage>;

  const frame = readFlat(drawn?.frame, PAGE_SCHEMA);
  const template = frame.template === 'auto' ? (drawn && drawn.modules.length ? 'landing' : 'article') : frame.template;
  // An item page set out as a case study has a column of facts beside its body, so it takes the
  // normal width rather than an article's narrow one.
  const caseStudy = !!current && !drawn && settings.detail.layout_by_type?.[ctx.typesById.get(current.item_type_id)?.key] === 'case_study';
  const width = frame.width === 'auto' ? (template === 'article' && !caseStudy ? 'narrow' : 'normal') : frame.width;
  const pageHeader = frame.page_header === 'auto' ? (template === 'landing' ? 'none' : 'title') : frame.page_header;
  const showChrome = !drawn || frame.chrome === 'site';
  const pageCtx = contextItem ? { ...ctx, contextItem } : ctx;
  const theme = settings.theme;
  const accent = theme.accent === 'auto' ? theme.primary : theme.accent;
  // Wherever text sits ON a brand colour it uses the readable pair (siteSettings.readablePair):
  // the colour deepened just enough for its text to reach 4.5:1. The exact brand colour stays for
  // accents that carry no text.
  const primaryPair = readablePair(theme.primary, theme.ink);
  const accentPair = readablePair(accent, theme.ink);
  const rootStyle = {
    '--site-primary': theme.primary,
    '--site-primary-fill': primaryPair.fill,
    '--site-on-primary': primaryPair.text,
    '--site-primary-text': readableText(theme.primary, theme.surface),
    '--site-accent': accent,
    '--site-accent-fill': accentPair.fill,
    '--site-on-accent': accentPair.text,
    '--site-surface': theme.surface,
    '--site-ink': theme.ink,
    '--site-radius': RADIUS[theme.radius],
    '--site-font-heading': fontStack(theme.font_heading),
    '--site-font-body': fontStack(theme.font_body),
    '--site-page-width': WIDTH[width],
  };

  // The form popup sits on <body>, not inside .site-root: the root is a size container, which makes it
  // the containing block for anything fixed inside it, so an overlay there would cover the whole page
  // rather than the screen. It carries the site's theme with it.
  const formPopup = formOpen && createPortal(
    <div className="site-form-layer" style={rootStyle}>
      <Suspense fallback={<div className="site-form-overlay"><div className="site-form-dialog"><div className="site-form-loading" aria-busy="true">Loading the form…</div></div></div>}>
        <SiteFormPopup key={formOpen.id} formId={formOpen.id} href={formOpen.href} onClose={closeForm} />
      </Suspense>
    </div>,
    document.body,
  );

  const filePopup = fileOpen && createPortal(
    <div className="site-form-layer" style={rootStyle}>
      <Suspense fallback={<div className="site-file-overlay"><div className="site-file-dialog" aria-busy="true" /></div>}>
        <SiteFileViewer key={fileOpen.href} href={fileOpen.href} kind={fileOpen.kind} title={fileOpen.title} onClose={closeFile} />
      </Suspense>
    </div>,
    document.body,
  );

  const signInPopup = signIn && createPortal(
    <div className="site-form-layer" style={rootStyle}>
      <Suspense fallback={null}>
        <SiteSignIn title={signIn.title} line={signIn.line} mark={signIn.mark} onClose={() => setSignIn(null)} onSignedIn={onSignedIn} />
      </Suspense>
    </div>,
    document.body,
  );

  // Present: the page's modules as slides, then on to the next page in header order.
  if (presenting && current && view && view.modules.length) {
    // The whole talk, for the timeline: every page in header order with its slide count (its modules,
    // then the slide that leads on).
    // Each page carries its slides in brief — a headline and a picture each, then the slide that leads
    // on — for the timeline's hover preview, and each slide's anchor, so a click can land on it.
    const order = presentOrder(siteRow, ctx.rowsById);
    const talk = order.map((id, at) => {
      const row = ctx.rowsById.get(id);
      const modules = pageViewOf(row)?.modules || [];
      const nextRow = order[at + 1] ? ctx.rowsById.get(order[at + 1]) : null;
      const brief = modules.map((m) => {
        const head = headOf(m, ctx.rowsById);
        return { anchor: m.anchor || m.id, headline: head.title || head.eyebrow || null, image: head.background || head.image || null };
      });
      brief.push({ anchor: '__end', headline: nextRow ? `Next: ${cf(nextRow).nav_label || nextRow.title}` : 'The end', image: null });
      return { id, title: cf(row).nav_label || row.title, slides: brief.length, brief };
    });
    const talkIndex = talk.findIndex((p) => p.id === current.id);
    // `slide`: land on that slide of the page; `atEnd`: on its last module, as going back does.
    const goPage = (i, { atEnd = false, slide = null } = {}) => {
      const page = talk[i];
      const row = page && ctx.rowsById.get(page.id);
      if (!row) return;
      const anchor = atEnd ? '__last' : slide != null && slide > 0 ? page.brief[slide]?.anchor : null;
      navigate(`${itemPath(workspaceSlug, row, ctx.homeId, ctx)}?present${anchor ? `#${encodeURIComponent(anchor)}` : ''}`);
    };
    const presentCtx = { ...ctx, present: true, fav: null };
    return (
      <div ref={rootRef} className="site-root site-root--presenting" style={rootStyle} onClick={onClick}>
        <Suspense fallback={null}>
          <SitePresent
            key={current.id}
            modules={view.modules}
            pageTitle={current.title}
            startId={location.hash ? decodeURIComponent(location.hash.slice(1)) : null}
            renderModule={(m, i) => <Section module={m} isFirst={i === 0} ctx={presentCtx} />}
            talk={talk}
            talkIndex={talkIndex}
            onGoPage={goPage}
            onExit={(anchor) => navigate(`${itemPath(workspaceSlug, current, ctx.homeId, ctx)}${anchor ? `#${encodeURIComponent(anchor)}` : ''}`)}
          />
        </Suspense>
        {formPopup}
        {filePopup}
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`site-root${embedded || memory ? ' site-root--contained' : ''}`} style={rootStyle} onClick={onClick}>
      <SiteHeader siteRow={siteRow} ctx={ctx} currentId={current?.id} show={showChrome} />
      <main className="site-main">
        {(!current || hiddenTemplate) && (
          <article className="site-article site-article--narrow">
            <h1>Not on this site</h1>
            <p>The page you followed may have moved. <a href={homeHref}>Go to the home page</a>.</p>
          </article>
        )}
        {current && drawn && !hiddenTemplate && (
          <>
            {pageHeader !== 'none' && (
              <article className={`site-article site-article--${width}`}>
                <h1>{(contextItem || current).title}</h1>
                {pageHeader === 'title_description' && (contextItem || current).description && <p className="site-article-lede">{labelMentions((contextItem || current).description)}</p>}
              </article>
            )}
            {drawn.modules.map((m, i) => <Section key={m.id || i} module={m} isFirst={i === 0 && pageHeader === 'none'} ctx={pageCtx} />)}
          </>
        )}
        {current && !drawn && <ItemPage row={current} ctx={ctx} />}
      </main>
      <SiteFooter siteRow={siteRow} ctx={ctx} show={showChrome} />
      {formPopup}
      {filePopup}
      {signInPopup}
      {showPill && (
        <Suspense fallback={null}>
          <EditSitePill spaceSlug={workspaceSlug} path={location.path} onPresent={() => navigate(`${location.path}?present`)} />
        </Suspense>
      )}
    </div>
  );
}
